// app/dashboard/page.tsx
'use client';

import React, { useEffect, useState } from 'react';
import Script from 'next/script';
import { DashboardProvider } from '@/contexts/DashboardContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FullPageLoading } from '@/src/components/component/loading-screen';
import { ModernDashboard } from '@/src/components/dashboard/ModernDashboard';
import { Button } from '@/components/ui/button';
import { ChevronRight, X } from 'lucide-react';
import { useRouter } from 'next/navigation';

// Add types for the datafeed without modifiers conflict
declare global {
  interface Window {
    tvReady?: boolean; // Optional, to match the existing declaration
    tvWidget?: any;
    globalDatafeedInstance?: any;

    // Debug function to check widget is accessible
    checkTvWidgetExists?: () => boolean;
    forceChartSymbolUpdate?: (symbol: string) => boolean;
  }
}

// Function to create a canvas monitor script with improved sizing
const createCanvasMonitorScript = () => {
  return `
  // Canvas and container size monitor script
  (function() {
    console.log('Enhanced canvas monitor activated');

    // Function to detect if the auth screen is present
    function hasAuthScreen() {
      const authScreens = document.querySelectorAll('.bg-slate-900.text-white.overflow-auto');
      const welcomeText = document.querySelectorAll('h1');
      for (let i = 0; i < welcomeText.length; i++) {
        if (welcomeText[i].textContent && welcomeText[i].textContent.includes('Welcome to Watch Inspect')) {
          return true;
        }
      }
      return authScreens.length > 0;
    }

    // Wait for the DOM to be fully loaded
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(fixTradingViewSizing, 500);
      setTimeout(setupSplitEvents, 500);
      setTimeout(fixAuthScreenIfPresent, 500);
    });

    // Special function to handle auth screen
    function fixAuthScreenIfPresent() {
      if (hasAuthScreen()) {
        console.log('Auth screen detected, applying specific fixes');

        // Find all chart containers
        const chartContainers = document.querySelectorAll('.chart-container, .chart-parent, .flex-1.h-full.bg-slate-900.relative');

        chartContainers.forEach(container => {
          // Find auth screen inside this container
          const authScreen = container.querySelector('.bg-slate-900.text-white.overflow-auto');
          if (authScreen) {
            // Use modern Tailwind classes with responsive considerations
            container.classList.add('h-auto', 'min-h-[100vh]', 'block', 'w-full'); 
            authScreen.classList.add('relative', 'h-auto', 'min-h-[100vh]', 'overflow-y-auto', 'w-full', 'max-w-full');

            // Ensure content within auth screen is displayed properly
            const contentWrapper = authScreen.querySelector('.relative.container.mx-auto');
            if (contentWrapper) {
              contentWrapper.classList.add('h-auto', 'block', 'w-full', 'py-8');
            }

            console.log('Auth screen layout fixed');
          }
        });
      } else {
        console.log('Auth screen not detected, applying trading view fixes');
        fixTradingViewSizing();
      }
    }

    // Function to fix all TradingView sizing issues
    function fixTradingViewSizing() {
      console.log('Fixing TradingView sizing issues');

      // Skip if auth screen is present
      if (hasAuthScreen()) {
        console.log('Auth screen detected, skipping TradingView sizing fixes');
        fixAuthScreenIfPresent();
        return;
      }

      // Fix iframe and container sizing
      const iframes = document.querySelectorAll('iframe[id^="tradingview_"]');
      iframes.forEach(iframe => {
        const container = iframe.closest('.chart-container') ||
                        iframe.closest('.min-h-0.p-3.pb-1 > div') ||
                        iframe.parentElement;

        if (container) {
          // Make sure container has appropriate size with improved responsiveness
          container.classList.add('h-full', 'min-h-[350px]', 'w-full', 'max-w-full');

          // Fix iframe sizing with better responsiveness
          iframe.classList.add('w-full', 'h-full', 'min-h-[350px]', 'object-contain');

          console.log('Fixed iframe and container sizes');
        }
      });

      // Fix canvas elements if they exist
      const canvases = document.querySelectorAll('canvas[aria-label*="Chart for"]');
      canvases.forEach(canvas => {
        canvas.classList.add('w-full', 'h-full', 'max-w-full', 'max-h-full', 'absolute', 'top-0', 'left-0', 'object-contain');
        console.log('Fixed canvas element styles');
      });

      // Fix h-screen issue with more adaptive solution
      const hScreenElements = document.querySelectorAll('.h-screen');
      hScreenElements.forEach(el => {
        if (el.classList.contains('flex.flex-col.md\\:flex-row')) {
          el.classList.remove('h-screen');
          el.classList.add('h-[100vh]', 'max-h-[100vh]', 'min-h-0');
          console.log('Fixed h-screen element with more responsive approach');
        }
      });

      // Fix Split.js containers with improved flex layout
      const splitContainers = document.querySelectorAll('.split-vertical > div, .split-horizontal > div');
      splitContainers.forEach(container => {
        container.classList.add('min-h-0', 'flex', 'flex-col', 'overflow-hidden', 'max-w-full', 'w-full');
        console.log('Fixed Split.js container styles');
      });

      // If we have access to the widget, resize it
      if (window.tvWidget && typeof window.tvWidget.resize === 'function') {
        try {
          // Find the container dimensions
          const chartContainer = document.querySelector('.chart-container') ||
                               document.querySelector('.min-h-0.p-3.pb-1 > div');

          if (chartContainer) {
            const rect = chartContainer.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              window.tvWidget.resize(rect.width, rect.height);
              console.log('Resized TradingView widget to:', rect.width, 'x', rect.height);
            }
          }
        } catch (err) {
          console.warn('Failed to resize widget:', err);
        }
      }
    }

    // Function to set up proper event handling for Split.js
    function setupSplitEvents() {
      console.log('Setting up Split.js event handling');

      // Skip if auth screen is present
      if (hasAuthScreen()) {
        console.log('Auth screen detected, skipping split events setup');
        return;
      }

      // Find all Split.js gutters
      const gutters = document.querySelectorAll('.gutter');
      gutters.forEach(gutter => {
        // Add mouseup and touchend event listeners to gutters
        gutter.addEventListener('mouseup', handleSplitResize);
        gutter.addEventListener('touchend', handleSplitResize);

        // Add visual handle element if not already present
        if (!gutter.querySelector('.gutter-handle')) {
          const handleElement = document.createElement('div');
          handleElement.className = 'gutter-handle';
          gutter.appendChild(handleElement);
          console.log('Added handle to gutter element');
        }

        // Determine if this is a vertical gutter or horizontal gutter
        const isVertical = gutter.parentElement?.classList.contains('split-vertical');
        if (isVertical) {
          gutter.classList.add('gutter-vertical', 'gutter-horizontal-style'); // Add class for vertical gutter and combined style

          // Style for horizontal bar (vertical split)
          gutter.classList.add('w-full', 'h-2', 'cursor-row-resize'); // Tailwind classes for width, height, cursor

          // Style the handle
          const handle = gutter.querySelector('.gutter-handle');
          if (handle) {
            handle.classList.add('absolute', 'left-[calc(50%-15px)]', 'top-1/2', '-translate-y-1/2', 'w-[30px]', 'h-[3px]', 'bg-blue-400/80', 'rounded-md'); // Tailwind classes for handle style
          }
        } else {
          gutter.classList.add('gutter-horizontal', 'gutter-vertical-style'); // Add class for horizontal gutter and combined style

          // Style for vertical bar (horizontal split)
          gutter.classList.add('w-2', 'h-full', 'cursor-col-resize'); // Tailwind classes for width, height, cursor

          // Style the handle
          const handle = gutter.querySelector('.gutter-handle');
          if (handle) {
            handle.classList.add('absolute', 'top-[calc(50%-15px)]', 'left-1/2', '-translate-x-1/2', 'h-[30px]', 'w-[3px]', 'bg-blue-400/80', 'rounded-md'); // Tailwind classes for handle style
          }
        }

        // Apply common gutter styles using Tailwind classes
        gutter.classList.add('bg-blue-500/20', 'transition-colors', 'duration-200', 'ease-in-out', 'z-[1001]', 'relative', 'pointer-events-auto', 'translate-z-0');

        gutter.addEventListener('mouseover', () => gutter.classList.add('bg-blue-500/50')); // Hover effect using class toggle
        gutter.addEventListener('mouseout', () => gutter.classList.remove('bg-blue-500/50'));
        gutter.addEventListener('mousedown', () => gutter.classList.add('bg-blue-500/50')); // Dragging effect using class toggle
        gutter.addEventListener('mouseup', () => gutter.classList.remove('bg-blue-500/50'));
        gutter.addEventListener('touchstart', () => gutter.classList.add('bg-blue-500/50'));
        gutter.addEventListener('touchend', () => gutter.classList.remove('bg-blue-500/50'));

        console.log('Added event listeners to gutter element');
      });

      // Create and dispatch a custom split-resize event
      function handleSplitResize() {
        console.log('Split resize detected');

        // Skip if auth screen is present
        if (hasAuthScreen()) {
          return;
        }

        // Dispatch custom event
        const event = new CustomEvent('split-resize');
        window.dispatchEvent(event);

        // Trigger resize fixes
        setTimeout(fixTradingViewSizing, 100);
        setTimeout(fixTradingViewSizing, 500); // Second attempt for reliability
      }
    }

    // Set up window resize handling
    window.addEventListener('resize', function() {
      setTimeout(hasAuthScreen() ? fixAuthScreenIfPresent : fixTradingViewSizing, 100);
    });

    // Monitor for login/logout changes
    const observer = new MutationObserver(function(mutations) {
      let authScreenAppeared = false;
      let authScreenDisappeared = false;
      let chartAppeared = false;

      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              // Check for auth screen appearance
              if (node.classList && node.classList.contains('bg-slate-900') &&
                  node.classList.contains('text-white') &&
                  node.classList.contains('overflow-auto')) {
                authScreenAppeared = true;
              }

              // Check for chart elements appearing
              if (node.tagName === 'CANVAS' || node.tagName === 'IFRAME' ||
                 (node.className && typeof node.className === 'string' &&
                 (node.className.includes('chart') || node.className.includes('tradingview')))) {
                chartAppeared = true;
              }
            }
          })
        }

        if (mutation.removedNodes.length) {
          mutation.removedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              // Check for auth screen disappearing
              if (node.classList && node.classList.contains('bg-slate-900') &&
                  node.classList.contains('text-white') &&
                  node.classList.contains('overflow-auto')) {
                authScreenDisappeared = true;
              }
            }
          });
        }
      })

      // Apply appropriate fixes based on what changed
      if (authScreenAppeared) {
        console.log('Auth screen appeared, applying auth screen fixes');
        setTimeout(fixAuthScreenIfPresent, 100);
      } else if (authScreenDisappeared || chartAppeared) {
        console.log('Auth screen disappeared or chart appeared, applying TradingView fixes');
        setTimeout(fixTradingViewSizing, 100);
      }
    });

    // Start observing the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'width', 'height']
    });

    // Initial fix after a short delay
    setTimeout(function() {
      if (hasAuthScreen()) {
        fixAuthScreenIfPresent();
      } else {
        fixTradingViewSizing();
      }
    }, 1000);

    // Apply fixes multiple times to catch delayed rendering
    setTimeout(function() {
      if (hasAuthScreen()) {
        fixAuthScreenIfPresent();
      } else {
        fixTradingViewSizing();
      }
    }, 2500);

    setTimeout(function() {
      if (hasAuthScreen()) {
        fixAuthScreenIfPresent();
      } else {
        fixTradingViewSizing();
      }
    }, 5000);
  })();
  `;
};

// Create a beautiful modal component for work in progress notice
const WorkInProgressModal = React.memo(
  ({ onClose }: { onClose: () => void }) => {
    const router = useRouter();

    // Quick navigation handler
    const navigateTo = (path: string) => {
      router.push(path);
      onClose();
    };

    // Navigation links with more subtle styling
    const navLinks = [
      { name: 'Marketplace', path: '/marketplace', icon: 'Store' },
      { name: 'Charts', path: '/charts', icon: 'LineChart' },
      { name: 'Forums', path: '/forums', icon: 'MessageSquare' },
      { name: 'Blog', path: '/blog', icon: 'FileText' },
      { name: 'About', path: '/about', icon: 'Info' },
    ];

    return (
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ pointerEvents: 'auto' }}
      >
        {/* Backdrop with blur effect */}
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{ pointerEvents: 'auto' }}
        />

        {/* Modal content */}
        <motion.div
          className="relative z-[10000] w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 p-6 shadow-xl"
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          style={{ pointerEvents: 'auto' }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50" />
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-blue-500/10 blur-[100px]" />
            <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-purple-500/10 blur-[100px]" />
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>

          {/* Content */}
          <div className="flex flex-col items-center text-center">
            <h3 className="text-xl font-bold text-white mb-1">
              Work in Progress
            </h3>

            <p className="text-white/70 mb-6">
              This dashboard is currently under development. Feel free to
              explore, but please note that some features may not be fully
              functional yet.
            </p>

            {/* Quick Links Section */}
            <div className="mb-6 w-full">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3 text-center">
                Quick Navigation
              </h4>
              <div className="grid grid-cols-5 gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.path}
                    onClick={() => navigateTo(link.path)}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-300 text-xs flex flex-col items-center justify-center border border-white/10 group"
                  >
                    <span className="group-hover:scale-105 transition-transform text-center">
                      {link.name}
                    </span>
                    <div className="mt-1 h-px w-0 group-hover:w-full bg-blue-500/40 transition-all duration-300" />
                  </button>
                ))}
              </div>
              <div className="mt-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              <Button
                onClick={onClose}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white border border-white/10 backdrop-blur-sm flex items-center justify-center gap-2 group"
              >
                Continue to Dashboard
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>

              <Button
                onClick={() => navigateTo('/')}
                className="flex-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 flex items-center justify-center gap-2 group"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }
);
WorkInProgressModal.displayName = 'WorkInProgressModal';

const DashboardPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isTvLibraryLoaded, setIsTvLibraryLoaded] = useState(false);
  const [hasTimeout, setHasTimeout] = useState(false);
  const [showWorkInProgressModal, setShowWorkInProgressModal] = useState(true);
  const [showDashboard, setShowDashboard] = useState(false);

  // Show dashboard only after modal is closed
  useEffect(() => {
    if (!showWorkInProgressModal) {
      setShowDashboard(true);
    }
  }, [showWorkInProgressModal]);

  // Setup failsafe timeout for loading state
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.log('Force-ending loading screen after timeout');
        setIsLoading(false);
        setHasTimeout(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  // Add a global debug function to access the widget
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.checkTvWidgetExists = () => {
        const exists = !!window.tvWidget;
        console.log(`TradingView widget exists: ${exists}`);
        if (exists) {
          console.log('Widget info:', {
            id: window.tvWidget._id,
            chartType: window.tvWidget._chartType,
            hasSymbol: !!window.tvWidget.symbolInterval,
          });
        }
        return exists;
      };
    }
  }, []);

  // Add the canvas monitor script to the page
  useEffect(() => {
    const script = document.createElement('script');
    script.textContent = createCanvasMonitorScript();
    document.head.appendChild(script);

    // Add patch for Split.js
    const splitPatchScript = document.createElement('script');
    splitPatchScript.textContent = `
        // Enhanced patch for Split.js behavior
        (function() {
          // Function to enhance Split.js gutter behavior
          function enhanceSplitBehavior() {
            console.log('Enhancing Split.js gutter behavior');

            // Function to apply proper styling to a gutter
            function styleGutter(gutter) {
              // Determine if this is a vertical or horizontal gutter
              const isVertical = gutter.parentElement?.classList.contains('split-vertical');

              gutter.classList.add('gutter', 'transition-colors', 'duration-200', 'ease-in-out', 'z-[1001]', 'relative', 'pointer-events-auto', 'translate-z-0');

              if (isVertical) {
                // This is a horizontal divider line
                gutter.classList.add('gutter-vertical');
                gutter.classList.add('w-full', 'h-2', 'cursor-row-resize', 'mt-[-4px]', 'mb-[-4px]'); // Tailwind classes
              } else {
                // This is a vertical divider line
                gutter.classList.add('gutter-horizontal');
                gutter.classList.add('w-2', 'h-full', 'cursor-col-resize', 'ml-[-4px]', 'mr-[-4px]'); // Tailwind classes
              }

              // Add handle element if missing
              if (!gutter.querySelector('.gutter-handle')) {
                const handle = document.createElement('div');
                handle.className = 'gutter-handle';
                handle.classList.add('gutter-handle', 'absolute', 'bg-blue-400/80', 'rounded-md', 'pointer-events-none'); // Tailwind classes for handle
                gutter.appendChild(handle);

                if (isVertical) {
                  handle.classList.add('left-[calc(50%-25px)]', 'top-1/2', '-translate-y-1/2', 'w-[50px]', 'h-[4px]'); // Tailwind classes for vertical handle
                } else {
                  handle.classList.add('top-[calc(50%-25px)]', 'left-1/2', '-translate-x-1/2', 'h-[50px]', 'w-[4px]'); // Tailwind classes for horizontal handle
                }
              }
            }

            // Patch Split.js create function to enhance gutters
            if (window.Split) {
              const originalSplit = window.Split;
              window.Split = function() {
                const instance = originalSplit.apply(this, arguments);

                // Get the arguments
                const args = Array.from(arguments);
                const options = args[1] || {};

                // Fix styles after a very short delay
                setTimeout(() => {
                  const gutters = document.querySelectorAll('.gutter');
                  gutters.forEach(styleGutter);

                  // Dispatch event to notify about gutter creation
                  window.dispatchEvent(new CustomEvent('gutters-created'));
                }, 10);

                // Enhance drag events
                const originalOnDragStart = instance.onDragStart;
                const originalOnDrag = instance.onDrag;
                const originalOnDragEnd = instance.onDragEnd;

                instance.onDragStart = function() {
                  const result = originalOnDragStart.apply(this, arguments);
                  document.body.classList.add('splitting');
                  return result;
                };

                instance.onDrag = function() {
                  const result = originalOnDrag.apply(this, arguments);
                  // Fix container sizes during drag for smoother resizing
                  window.dispatchEvent(new CustomEvent('split-resize'));
                  return result;
                };

                instance.onDragEnd = function() {
                  const result = originalOnDragEnd.apply(this, arguments);
                  document.body.classList.remove('splitting');

                  // Fix everything after drag ends
                  window.dispatchEvent(new CustomEvent('split-resize'));

                  // Double call with delay for reliability
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('split-resize'));
                  }, 200);

                  return result;
                };

                return instance;
              };

              // Preserve prototype and properties
              Object.setPrototypeOf(window.Split, originalSplit);
              Object.keys(originalSplit).forEach(key => {
                window.Split[key] = originalSplit[key];
              });

              console.log('Split.js patched successfully');
            }
          };

          // Run enhancement when document is ready
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', enhanceSplitBehavior);
          } else {
            enhanceSplitBehavior();
          }

          // Also try to apply on any existing gutters
          setTimeout(() => {
            const gutters = document.querySelectorAll('.gutter');
            console.log('Found', gutters.length, 'existing gutters to enhance');
            gutters.forEach(gutter => {
               // Determine if this is a vertical or horizontal gutter
              const isVertical = gutter.parentElement?.classList.contains('split-vertical');
              if (isVertical) {
                gutter.classList.add('gutter-vertical');
              } else {
                gutter.classList.add('gutter-horizontal');
              }
            });
          }, 1000);
        })();
      `;
    document.head.appendChild(splitPatchScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(splitPatchScript);
    };
  }, []);

  // Handle TradingView script load
  const handleTvScriptLoad = () => {
    console.log('🔍 TradingView library loaded via Script component');

    // Set window property
    window.tvReady = true;

    setIsTvLibraryLoaded(true);

    // Dispatch an event for components listening
    window.dispatchEvent(new Event('tvLibraryLoaded'));
  };

  // Make sure the global datafeed instance is available when needed
  useEffect(() => {
    // Function to check if globalDatafeedInstance exists
    const checkDatafeed = () => {
      if (window.globalDatafeedInstance) {
        console.log('Global datafeed instance found');
        return true;
      }
      return false;
    };

    // If not found, check every 200ms for 5 seconds
    if (!checkDatafeed()) {
      const intervalId = setInterval(() => {
        if (checkDatafeed()) {
          clearInterval(intervalId);
        }
      }, 200);

      // Clean up after 5 seconds
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        console.warn('Could not find globalDatafeedInstance within timeout');
      }, 5000);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  return (
    <>
      {/* Load TradingView library */}
      <Script
        src="/charting_library-master/charting_library/charting_library.standalone.js"
        strategy="afterInteractive"
        onLoad={handleTvScriptLoad}
        onError={(e) => console.error('Failed to load TradingView library:', e)}
      />

      {/* Work in Progress Modal - Moved before DashboardProvider */}
      <AnimatePresence>
        {showWorkInProgressModal && (
          <WorkInProgressModal
            onClose={() => setShowWorkInProgressModal(false)}
          />
        )}
      </AnimatePresence>

      {showDashboard && (
        <DashboardProvider>
          <ModernDashboard
            onLoadComplete={() => {
              console.log('Dashboard load complete, hiding loading screen');
              setIsLoading(false);
            }}
            isTvLibraryReady={isTvLibraryLoaded}
          />

          {/* Global loading screen with animation */}
          <AnimatePresence mode="wait">
            {isLoading && (
              <FullPageLoading
                message={
                  hasTimeout
                    ? 'Taking longer than expected...'
                    : 'Loading market data...'
                }
              />
            )}
          </AnimatePresence>

          {/* Show help text if loading takes too long */}
          {isLoading && hasTimeout && (
            <motion.div
              className="fixed bottom-10 left-0 right-0 text-blue-400/80 text-xs max-w-md text-center mx-auto z-[9999]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p>
                If the dashboard doesn&apos;t load, try refreshing the page.
              </p>
            </motion.div>
          )}
        </DashboardProvider>
      )}

      {/* Force a data range prefetch for common symbols */}
      <Script id="prefetch-symbols" strategy="afterInteractive">
        {`
          try {
            console.log('Pre-fetching common symbol data ranges');
            // Using correct case for Rolex models based on API
            const symbols = ['Rolex:16610', 'Rolex:16710', 'Rolex:116520', 'METAL:GOLD', 'INDEX:SP500', 'INFLATION:RATE'];
            // Fetch each symbol's data range in parallel
            Promise.all(symbols.map(symbol =>
              fetch('/api/trading-dashboard/getDataRange?symbol=' + encodeURIComponent(symbol) + '&source=forum')
                .then(res => res.ok ? res.json() : null)
                .then(data => {
                  if (data) console.log('Pre-fetched data range for ' + symbol);
                  return data;
                })
                .catch(err => console.warn('Failed to pre-fetch ' + symbol, err))
            )).then(() => {
              console.log('All pre-fetch operations completed');
            });
          } catch (e) {
            console.error('Error in pre-fetch script:', e);
          }
        `}
      </Script>

      {/* Add custom debug script */}
      <Script id="chart-debug" strategy="afterInteractive">
        {`
    try {
      // Function to detect if the auth screen is present
      function hasAuthScreen() {
        const authScreens = document.querySelectorAll('.bg-slate-900.text-white.overflow-auto');
        const welcomeText = document.querySelectorAll('h1');
        for (let i = 0; i < welcomeText.length; i++) {
          if (welcomeText[i].textContent && welcomeText[i].textContent.includes('Welcome to Watch Inspect')) {
            return true;
          }
        }
        return authScreens.length > 0;
      }

      // Create a global helper function to update chart symbol
// Create a global helper function to update chart symbol
window.forceChartSymbolUpdate = function(symbol) {
  // Skip if auth screen is present
  if (hasAuthScreen()) {
    console.log('Auth screen detected, skipping chart symbol update');
    return false;
  }

  console.log('Force update chart to symbol:', symbol);

  if (window.tvWidget) {
    try {
      const interval = window.tvWidget.symbolInterval ? window.tvWidget.symbolInterval().interval : '1D';

      // Reset symbol to force a complete reload
      window.tvWidget.setSymbol('', interval, function() {
        // Then set to new symbol with a delay - 500ms matches your working code
        setTimeout(function() {
          window.tvWidget.setSymbol(symbol, interval, function() {
            console.log('Chart symbol updated via global helper to:', symbol);
          });
        }, 500);
      });

      return true;
    } catch (err) {
      console.error('Error updating chart symbol:', err);
      return false;
    }
  } else {
    console.error('TradingView widget not found!');
    return false;
  }
};

// Setup a listener for the widget ready event
window.addEventListener('tvWidgetReady', function(e) {
  console.log('tvWidgetReady event detected!');
  if (e.detail && e.detail.widget) {
    window.tvWidget = e.detail.widget;
    console.log('Set global tvWidget from ready event');
  }
});

// Add global event listener for our custom event
document.addEventListener('watch-symbol-selected', function(e) {
  // Skip if auth screen is present
  if (hasAuthScreen()) {
    return;
  }

  if (e && e.detail && e.detail.symbol) {
    console.log('Global event listener caught symbol change:', e.detail.symbol);
    window.forceChartSymbolUpdate(e.detail.symbol);
  }
});

console.log('Chart debug helpers loaded');
} catch (e) {
  console.error('Error in chart debug script:', e);
}
`}
      </Script>

      {/* Add the script for additional layout fixes */}
      <Script id="layout-fixes" strategy="afterInteractive">
        {`
    // Additional layout fixes that need to run after DOM is ready
    document.addEventListener('DOMContentLoaded', function() {
      // Fix for auth screen in chart container
      function fixAuthScreenInChart() {
        const authScreens = document.querySelectorAll('.bg-slate-900.text-white.overflow-auto');
        authScreens.forEach(screen => {
          const chartParent = screen.closest('.chart-parent');
          if (chartParent) {
            // Adjust chart parent to accommodate auth screen using Tailwind classes
            chartParent.classList.add('h-auto', 'overflow-y-auto');

            console.log('Fixed auth screen in chart container');

            // Find the content wrapper in auth screen and limit its height
            const contentWrapper = screen.querySelector('.relative.container.mx-auto');
            if (contentWrapper) {
              contentWrapper.classList.add('pt-8', 'pb-8'); // Tailwind classes for padding-top and padding-bottom (2rem = 8 in default scale)
            }
          }
        });
      }

      // Run immediately and after a delay
      fixAuthScreenInChart();
      setTimeout(fixAuthScreenInChart, 1000);
    });
  `}
      </Script>
    </>
  );
};

export default DashboardPage;
